import React, { useState } from 'react';
import { 
  GraduationCap, CheckCircle2, QrCode, CreditCard, 
  BookOpen, ArrowRight, Printer, ShieldCheck, User 
} from 'lucide-react';
import { INSTITUTION_INFO, DEMO_STUDENTS } from '../../data/portalData';

export const EnrolmentPortal = ({ setActiveView, setSelectedStudent }) => {
  const [selectedTrack, setSelectedTrack] = useState('student-dba');
  const [coursesConfirmed, setCoursesConfirmed] = useState(true);

  const currentStudent = DEMO_STUDENTS.find(s => s.id === selectedTrack) || DEMO_STUDENTS[1];

  const handleLaunchLms = () => {
    if (setSelectedStudent) {
      setSelectedStudent(currentStudent.id);
    }
    setActiveView('lms-student');
  };

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 sm:px-6 space-y-8">
      
      {/* Top Banner in AKSU Crimson Red and Forest Green */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-2xl p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4 shadow-xl border-2 border-[#7A470E]">
        <div>
          <div className="flex items-center space-x-2 text-xs font-black uppercase tracking-wider text-[#B87A28] font-sc">
            <GraduationCap className="w-4 h-4" />
            <span>Matriculation & Enrolment Desk</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-black font-sc mt-1 text-white">
            Student Enrolment & Course Registration
          </h2>
          <p className="text-xs sm:text-sm text-slate-200 mt-1 max-w-xl">
            Confirm your semester modules, download your official executive digital identity card, and access the LMS environment.
          </p>
        </div>

        {/* Student Selector Pill */}
        <div className="flex items-center space-x-2 bg-[#030F1C] p-1.5 rounded-xl border border-slate-800 text-xs">
          <span className="text-slate-300 px-2 font-bold font-sc">Switch Track:</span>
          {DEMO_STUDENTS.map(s => (
            <button
              key={s.id}
              onClick={() => setSelectedTrack(s.id)}
              className={`px-3 py-1.5 rounded-lg font-black transition-all cursor-pointer font-sc ${
                selectedTrack === s.id
                  ? 'bg-[#7A470E] text-white shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-[#3A0205]'
              }`}
            >
              {s.degreeLevel}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left: Registered Semester Modules */}
        <div className="lg:col-span-7 bg-white rounded-2xl border-2 border-slate-200 p-6 sm:p-8 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b-2 border-slate-200 pb-4">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block font-sc">
                {currentStudent.department}
              </span>
              <h3 className="text-2xl font-black font-sc text-[#0B2644]">
                {currentStudent.programmeName}
              </h3>
              <div className="text-xs text-slate-500 mt-0.5">
                Session: 2025/2026 Executive Semester | Matric: <strong className="font-mono text-[#7A470E] font-bold">{currentStudent.matricNo}</strong>
              </div>
            </div>
            <span className="px-2.5 py-1 text-xs font-black rounded-lg bg-slate-100 text-[#7A470E] font-sc">
              Enrolled
            </span>
          </div>

          <div className="space-y-3">
            <span className="text-xs font-black uppercase tracking-wider text-slate-700 block font-sc">
              Official Registered Courses & Credit Units:
            </span>

            <div className="space-y-2.5">
              {currentStudent.modules.map((m) => (
                <div 
                  key={m.code}
                  className="p-3.5 rounded-xl bg-slate-50 border-2 border-slate-200 flex items-center justify-between text-xs hover:border-[#7A470E] transition-colors"
                >
                  <div className="space-y-0.5 pr-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono font-bold text-[#0B2644] bg-amber-100 px-1.5 py-0.5 rounded">
                        {m.code}
                      </span>
                      <span className="font-bold text-slate-900">{m.title}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Lecturer: {m.lecturer} • {m.schedule}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-xs font-extrabold text-[#7A470E] block">{m.units} Units</span>
                    <span className="text-[10px] text-emerald-700 font-bold">Active</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3.5 bg-amber-50/50 rounded-xl border border-slate-200 flex items-center justify-between text-xs text-red-950 font-medium">
              <span className="font-sc font-bold">Total Semester Registered Credit Load:</span>
              <strong className="text-base font-black font-mono text-[#0B2644]">
                {currentStudent.modules.reduce((acc, curr) => acc + curr.units, 0)} Credit Units
              </strong>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
            <div className="text-xs text-slate-600 flex items-center">
              <CheckCircle2 className="w-4 h-4 text-[#7A470E] mr-1.5" />
              <span>Admissions Clearance & Senate Registration Complete</span>
            </div>

            <button
              onClick={handleLaunchLms}
              className="px-6 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold rounded-xl text-xs sm:text-sm shadow-md transition-all flex items-center space-x-2 cursor-pointer font-sc"
            >
              <span>Launch Student LMS</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right: Official Executive Student Identity Card in University Brand Colors */}
        <div className="lg:col-span-5 space-y-4">
          <div className="text-xs font-black uppercase tracking-wider text-[#7A470E] text-center font-sc">
            Official Student Identity Card
          </div>

          {/* ID Card Front */}
          <div className="bg-gradient-to-br from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-2xl p-5 border-4 border-[#7A470E] shadow-2xl relative overflow-hidden">
            {/* Ambient Glow */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#7A470E]/20 rounded-full blur-xl pointer-events-none" />

            {/* ID Card Header Strip in AKSU Colors */}
            <div className="flex items-center justify-between border-b-2 border-[#7A470E] pb-3">
              <div className="flex items-center space-x-2.5">
                <img src="/Logo.png" alt="AKSU Logo" className="w-12 h-12 object-contain logo-glow select-none" />
                <div>
                  <div className="text-[10px] uppercase font-black tracking-wider text-[#B87A28] font-sc">
                    Akwa Ibom State University
                  </div>
                  <div className="text-sm font-black font-sc text-[#B87A28]">
                    BUSINESS SCHOOL
                  </div>
                </div>
              </div>
              <span className="text-[9px] font-black bg-[#7A470E] text-white px-2 py-0.5 rounded font-sc shadow">
                STUDENT ID
              </span>
            </div>

            {/* ID Card Body */}
            <div className="py-4 flex items-center space-x-4">
              <img 
                src={currentStudent.avatar} 
                alt={currentStudent.fullName} 
                className="w-20 h-24 rounded-lg object-cover border-2 border-[#7A470E] shadow-md shrink-0"
              />

              <div className="space-y-1 text-xs">
                <div className="font-sc font-black text-base text-white leading-tight">
                  {currentStudent.title && !currentStudent.title.toLowerCase().includes('executive') ? `${currentStudent.title} ` : ''}{currentStudent.fullName}
                </div>
                <div className="text-[11px] text-[#B87A28] font-bold font-sc">
                  {currentStudent.degreeLevel} Scholar
                </div>
                <div className="text-[10px] text-slate-300 line-clamp-1">
                  {currentStudent.programmeName}
                </div>
                <div className="pt-1 text-[11px] font-mono">
                  <span className="text-red-400">MATRIC: </span>
                  <span className="text-white font-bold">{currentStudent.matricNo}</span>
                </div>
              </div>
            </div>

            {/* ID Card Footer in AKSU Green & White */}
            <div className="border-t border-[#7A470E]/40 pt-2.5 flex items-center justify-between text-[9px] text-slate-300">
              <div>
                <span className="block font-bold text-[#B87A28] font-sc">VALID THROUGH: 2028</span>
                <span className="text-slate-400 font-sc">KNOWLEDGE · ENTERPRISE · LEADERSHIP</span>
              </div>
              <QrCode className="w-8 h-8 text-white opacity-90" />
            </div>
          </div>

          {/* Quick ID Card Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="flex-1 py-2 px-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-xl border border-slate-200 text-xs shadow-xs transition-colors flex items-center justify-center space-x-1.5 cursor-pointer font-sc"
            >
              <Printer className="w-3.5 h-3.5 text-[#0B2644]" />
              <span>Print ID Card</span>
            </button>
            <button
              onClick={handleLaunchLms}
              className="flex-1 py-2 px-3 bg-[#7A470E] hover:bg-[#5C3306] text-white font-black rounded-xl text-xs shadow-sm transition-colors flex items-center justify-center space-x-1.5 cursor-pointer font-sc"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Open Course Modules</span>
            </button>
          </div>

        </div>

      </div>

    </div>
  );
};
